/**
 * Live token-update APIs: per-session token stats and the global token
 * analytics room.
 *
 * subscribeToSession throws (rather than warn-and-noop) when no socket
 * exists — its consumers connect before subscribing and treat a missing
 * socket as a programming error, unlike the room-based domains.
 */

import type { SessionTokenStatsResponse } from '@/types/tokens'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

interface TokenUpdateEvent {
  session_id: string
  data: SessionTokenStatsResponse
}

export class TokensLiveApi {
  private readonly sessions: SubscriptionRegistry<string, SessionTokenStatsResponse>
  private readonly globalUpdates: SubscriptionRegistry<null, unknown>

  constructor(private readonly client: WebSocketClient) {
    // Session and global-analytics joins are emitted per subscribe call (the
    // server-side join is idempotent), matching the long-standing contract.
    this.sessions = new SubscriptionRegistry({
      join: (sessionId) => this.client.emitWhenConnected('join', { sessionId }),
      leave: (sessionId) => this.client.emitIfSocket('leave', { sessionId }),
      joinOnEverySubscribe: true,
    })
    this.globalUpdates = new SubscriptionRegistry({
      join: () => this.client.emitWhenConnected('join_global_analytics', {}),
      leave: () => this.client.emitIfSocket('leave_global_analytics', {}),
      joinOnEverySubscribe: true,
    })
  }

  /**
   * Subscribe to real-time token updates for one session. Returns the
   * cleanup function that unsubscribes and leaves the room when last out.
   */
  subscribeToSession(
    sessionId: string,
    callback: (data: SessionTokenStatsResponse) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      throw new Error('WebSocket not connected. Call connect() first.')
    }

    console.log('Subscribing to session:', sessionId)

    this.client.attachOnce('token_update', (event) => {
      const { session_id, data } = event as TokenUpdateEvent
      if (!this.sessions.hasSubscribers(session_id)) return

      console.log('Received token update for session:', session_id, data)
      this.sessions.dispatch(session_id, data)
    })

    const unsubscribe = this.sessions.subscribe(sessionId, callback)

    return () => {
      if (!this.client.hasSocket()) return
      console.log('Unsubscribing from session:', sessionId)
      unsubscribe()
    }
  }

  /** Subscribe to global token analytics updates. */
  subscribeToGlobalTokenUpdates(callback: (data: unknown) => void): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    this.client.attachOnce('global_token_update', (event) => {
      this.globalUpdates.dispatch(null, event)
    })

    return this.globalUpdates.subscribe(null, callback)
  }

  /** Rejoin both token rooms after a reconnect. */
  replay(): void {
    this.sessions.replay()
    this.globalUpdates.replay()
  }

  clear(): void {
    this.sessions.clear()
    this.globalUpdates.clear()
  }
}
